<template>
    <div class="container">
        <div v-if="hide">
            <h2>Review Your Order</h2>
            <p class="lead">
                <span v-if="user">Hello {{ user.name }}.</span>

                Your order for {{ orderSize }} gallons of oil will be charged to your credit card.

                <span v-if="orderSize > 150">If your actual delivery is less than {{ orderSize }} gallons, any balance remaining will be refunded after the delivery has been made.</span>
            </p>
            <p class="lead">
                Your order will be delivered on or before <em>{{ deliveryDate }}</em>.
            </p>
        </div>

        <div class="shadow mb-4">
            <div class="row ml-0 mr-0">
                <div class="col-sm review-order order-delivery-date">
                    {{ deliveryDate }}<br>
                    <span class="normal">due by date</span>
                </div>
                <div class="col-sm review-order order-address">
                    {{ street }}<br>
                    {{ cityStateZip }}
                </div>
                <div class="col-sm review-order order-amount">
                    {{ orderSize }} gallons<br>
                    <span class="normal">at ${{ gallonPrice }}/gallon</span>
                </div>
                <div class="col-sm review-price">
                    <span class="your-total">Your Total</span>
                    ${{ estimatedPrice }}
                </div>
            </div>
        </div>
            
        <h3>Checkout</h3>

        <p class="lead">
            <em>Review your complete order details below.</em>
        </p>

        <div v-if="!user" class="alert text-center mb-5">
            <p class="lead">
                To place your order, please log in or create an account. If you’ve ever placed an order,
                you can log in with your email address.
            </p>

            <div>
                <a class="btn btn-primary" href="/order/login?to=/order/review">Log In</a>
                <a class="btn btn-secondary ml-3" href="/order/register">Create an account</a>
            </div>
        </div>

        <div v-if="user" class="checkout">
            <div class="alert alert-danger" v-if="checkoutError">{{ checkoutError }}</div>
            <div v-if="errors.items.length" class="alert alert-danger">
                Please correct the following errors:
                <ul>
                    <li v-for="err in errors.items" v-text="err.msg.replace(/_/g, ' ')"></li>
                </ul>
            </div>

            <div v-if="$root.allow_skipping_payment" class="alert alert-info">
                <p>
                    Click here if you'd like to skip payment at this time and settle this account separately.
                </p>
                <div class="form-check">
                    <label class="form-check-label">
                        <input id="automatic-emails" class="form-check-input" type="checkbox" v-model="order.no_emails">
                        Check here if this is for an automatic delivery to skip sending emails to the customer.
                    </label>
                </div>
                <button class="btn btn-primary" @click.prevent="placeOrder(true)" :class="{submitting}">>Skip Payment</button>
            </div>

            <form method="post">
                <div class="form-row">
                    <div class="col-md-3">
                        <div class="form-group" :class="{invalid: errors.first('first_name_on_card')}">
                            <label class="form-control-label label">First Name on Card</label>
                            <input class="form-control form-control-lg" :class="{'is-invalid': errors.first('first_name_on_card')}" type="text" name="first_name_on_card" v-model="card.first_name_on_card" />
                        </div>
                    </div>
                    <div class="col-md-3">
                        <div class="form-group" :class="{invalid: errors.first('last_name_on_card')}">
                            <label class="form-control-label label">Last Name on Card</label>
                            <input class="form-control form-control-lg" :class="{'is-invalid': errors.first('last_name_on_card')}" type="text" name="last_name_on_card" v-model="card.last_name_on_card" />
                        </div>
                    </div>
                    <div class="col-md-6">
                        <div class="form-group" :class="{invalid: errors.first('billing_zip_code')}">
                            <label class="form-control-label label">Billing Zip Code</label>
                            <input class="form-control form-control-lg" :class="{'is-invalid': errors.first('billing_zip_code')}" type="text" name="billing_zip_code" v-model="card.billing_zip_code" />
                        </div>
                    </div>
                </div>

                <div class="form-row">
                    <div class="col-md-5">
                        <div class="form-group" :class="{invalid: errors.first('card_number')}">
                            <div class="d-flex justify-content-between">
                                <label for="card_number" class="form-control-label label">Credit Card Number</label>
                                <div class="credit-cards d-flex">
                                    <div class="icon ml-1">
                                        <img class="img-fluid amex" src="/order/img/icon-amex.png" alt="AMEX accepted" />
                                    </div>
                                    <div class="icon ml-1">
                                        <img class="img-fluid visa" src="/order/img/icon-visa.png" alt="Visa accepted" />
                                    </div>
                                    <div class="icon ml-1">
                                        <img class="img-fluid mastercard" src="/order/img/icon-mastercard.png" alt="MasterCard accepted" />
                                    </div>
                                    <div class="icon ml-1">
                                        <img class="img-fluid discover" src="/order/img/icon-discover.png" alt="Discover accepted" />
                                    </div>
                                </div>
                            </div>
                            <input class="form-control form-control-lg" :class="{'is-invalid': errors.first('card_number')}" id="card_number" type="text" name="card_number" v-model="card.card_number" placeholder="" />
                        </div>
                    </div>
                    <div class="col-md">
                        <div class="form-group" :class="{invalid: errors.first('validation_code')}">
                            <div class="d-flex justify-content-between">
                                <label class="form-control-label label">Security Code</label>
                                <div class="tip">
                                    <div
                                        data-toggle="popover"
                                        data-placement="top"
                                        data-content="
                                        <p>Visa and Mastercard codes are found on the back (three digits).</p>
                                        <p>American Express code is found on the front (four digits).</p>
                                        <div><img src='/order/img/cvv.png' alt='' /></div>
                                    ">
                                      <i class="fa fa-question-circle trigger" aria-hidden="true"></i>
                                    </div>
                                </div>
                            </div>
                            <input class="form-control form-control-lg" :class="{'is-invalid': errors.first('validation_code')}" type="text" name="validation_code" v-model="card.validation_code" placeholder="" />
                        </div>
                    </div>
                    <div class="col-md">
                        <div class="form-group" :class="{invalid: hasExpirationError}">
                            <label class="form-control-label label">Expiration Month</label>
                            <select class="form-control form-control-lg custom-select" :class="{'is-invalid': errors.first('expiration_month')}" name="expiration_month" id="" v-model="card.expiration_month">
                                <option disabled value=""></option>
                                <option value="01">January</option>
                                <option value="02">February</option>
                                <option value="03">March</option>
                                <option value="04">April</option>
                                <option value="05">May</option>
                                <option value="06">June</option>
                                <option value="07">July</option>
                                <option value="08">August</option>
                                <option value="09">September</option>
                                <option value="10">October</option>
                                <option value="11">November</option>
                                <option value="12">December</option>
                            </select>
                        </div>
                    </div>
                    <div class="col-md">
                        <div class="form-group">
                            <label class="form-control-label label">Expiration Year</label>
                            <select class="form-control form-control-lg custom-select" :class="{'is-invalid': errors.first('expiration_year')}" name="expiration_year" v-model="card.expiration_year">
                                <option disabled value=""></option>
                                <option v-for="year in years" v-bind:value="year" v-text="year"></option>
                            </select>
                        </div>
                    </div>
                </div>
            </form>
        </div>

        <div class="submit">
            <div v-if="zipCode && zipCode.tank_inspection_message" class="alert alert-warning">
                <p v-text="zipCode.tank_inspection_message"></p>
            </div>
            <div class="alert alert-warning">
                By submitting an order I agree to to a 4% cancellation fee. The 4% cancellation fee will apply for any reason the order is canceled and is based on the total sales price.
            </div>
            <p>
                <label>
                    <input class="mr-2" type="checkbox" v-model="card.agree_to_terms"> I agree to the <a href="/about-us/terms-conditions" class="new-window" target="_blank"> Terms &amp; Conditions</a> of the Website
                </label>
            </p>
            <div class="alert alert-warning">
                Please click Place Order only ONCE. Repeated clicking may result in multiple orders.
            </div>
            <div class="mb-5">
                <button v-if="user" @click.prevent="validate" class="btn btn-primary btn-lg" :class="{submitting}">Place Order</button>
            </div>

            <receipt></receipt>

            <router-link class="btn btn-link btn-back mt-4 p-0" :to="{ name: 'form' }">Go Back</router-link>
        </div>
    </div>
</template>
<script>
import Receipt from './Receipt';
import VeeValidate from 'vee-validate';
import _ from 'lodash';
import moment from 'moment';
import numeral from 'numeral';

const Validator = VeeValidate.Validator;

export default {
    validator: null,
    components: {Receipt},

    data() {
        return {
            hide: false,
            card: {
                first_name_on_card: '',
                last_name_on_card: '',
                billing_zip_code: '',
                card_number: '',
                validation_code: '',
                expiration_month: '',
                expiration_year: '',
                description: '',
                amount: 0,
                agree_to_terms: null,
            },
            errors: {},
            checkoutError:'',
            submitting: false,
        };
    },

    computed: {
        orderSize() {
            if (this.order.fill_tank && this.order.tank_size) {
                return this.order.tank_size;
            }
            if (this.order.gallons) {
                return this.order.gallons;
            }
            return this.order.tank_size;
        },

        priceSurcharge() {
            return this.$root.priceSurcharge(this.order.gallons);
        },

        gallonPrice() {
            return this.zone ? numeral(this.zone.price).add(this.priceSurcharge).format('0.000') : null;
        },

        order() {
            return this.$root.order;
        },

        zipCode() {
            return this.$root.zipCode;
        },

        zone() {
            return this.zipCode && this.zipCode.zone;
        },

        street() {
            return this.order.street;
        },

        cityStateZip() {
            if (this.zipCode) {
                return this.zipCode.city + ', ' + this.zipCode.state.name + ' ' + this.zipCode.zip_code;
            }

            return '';
        },

        user() {
            return this.$root.user;
        },

        estimate() {
            return this.$root.estimate;
        },

        deliveryDate() {
            return moment(this.estimate['delivery_date']).format('dddd, MMMM Do');
        },

        estimatedPrice() {
            return this.$root.estimate['total'];
        },

        orderDescription() {

            return 'Order for ' + this.orderSize + ' gallons of heating oil for delivery to ' + this.order.street + ' ' + this.zipCode.city + ', ' + this.zipCode.state.name + '.';
        },

        ccRules() {
            let rules = {
                first_name_on_card: { rules: { required: true } },
                last_name_on_card: { rules: { required: true } },
                card_number: { rules: { required: true } },
                billing_zip_code: { rules: { required: true } },
                validation_code: { rules: { required: true } },
                expiration_month: { rules: { required: true } },
                expiration_year: { rules: { required: true } },
                agree_to_terms: { rules: { required: true } }
            };

            return rules;
        },

        hasExpirationError() {
            if (this.errors.first('expiration_month')) {
                return true;
            }
            if (this.errors.first('expiration_year')) {
                return true;
            }
        },

        years() {
            var year = parseInt(new Date().getFullYear());

            return [0,1,2,3,4,5,6,7,8,9].map(n => n + year);
        },

        firstStepComplete() {
            let o = this.order;
            return (
                o.street && o.cross_street && o.zip_code && o.ownership &&
                o.property_type && o.location && o.gauge_reading &&
                o.agree_to_terms && o.gallons
            );
        },
    },

    created() {
        // Refresh estimate to get the current zone pricing
        this.refreshEstimate();

        this.clearConfirmationLoaded();

        // Form Validation
        this.validator = new Validator();

        // should PR this lib to allow for rules
        // in the object format to be passed to the constructor
        _.forOwn(this.ccRules, (field, name) => {
            this.validator.attach(name, field.rules);
        });
        this.errors = this.validator.errors;
    },

    methods: {
        clearConfirmationLoaded() {
            if (localStorage.getItem('confirmationLoaded') == 1 || sessionStorage.getItem('confirmationLoaded') == 1) {
                localStorage.setItem('confirmationLoaded', 0);
                sessionStorage.setItem('confirmationLoaded', 0);
            }
        },
        checkPriceStillValid(callback) {
            axios.get('/order/api/price', {params: {zip_code: this.order.zip_code}})
                .then(response => {
                    let price = response.data.data.current_price;
                    if (response.data.data.valid) {
                        return callback();
                    } else if (parseFloat(this.gallonPrice) == price) {
                        return callback();
                    } else {
                        // This is hacky
                        this.zipCode.price = price;
                        this.zipCode.zone.price = price;
                        this.zone.price = price;
                        this.gallonPrice = price;
                        this.submitting = false;

                        this.errors.items.push({
                            msg: `The price has expired after 30 minutes. The current price is ${this.gallonPrice} if you continue.`
                        });
                    }
                });
        },
        validate() {
            if (this.submitting) {
                return;
            }

            this.validator.validateAll(this.card)
                .then(result => {
                    if (result) {
                        this.checkPriceStillValid(this.placeOrder);
                    } else {
                        this.submitting = false;
                    }
                });
        },

        guessNames(name) {
            let bits = name.replace(/( (sr\.?|jr\.?|i|ii|iii|iv|v|vi|vii|viii|ix|x))?$/i, '').split(' ');
            let lastname = bits.pop();
            return [bits.join(' '), lastname];
        },

        placeOrder(automaticPayment) {
            var order  = this.order;
            var error;

            if (this.submitting) {
                return;
            }

            this.submitting = true;

            this.checkoutError = '';
            this.order['transaction_id'] = '';
            this.order['invoice_number'] = '';
            this.order['zip_code']       = this.zipCode.zip_code;
            this.order['company_id']     = this.zipCode.zone.company_id;
            this.card['amount']          = this.estimatedPrice;
            this.card['description']     = this.orderDescription;

            if (automaticPayment) {
                [this.card.first_name_on_card, this.card.last_name_on_card] = this.guessNames(this.$root.user.name);
                this.$root.order.first_name_on_card = this.card.first_name_on_card;
                this.$root.order.last_name_on_card = this.card.last_name_on_card;
                this.card.agree_to_terms = true;
            }

            try {
                if (this.card.first_name_on_card) {
                    order.first_name_on_card = this.card.first_name_on_card;
                }
                if (this.card.last_name_on_card) {
                    order.last_name_on_card = this.card.last_name_on_card;
                }

                // Process credit card
                axios.post('/order/api/process', {...this.order, ...this.card, automaticPayment})
                    .then(response => {
                        if ([200, 201].includes(response.status)) {
                            if (response.data.data.error_message) {
                                this.checkoutError = response.data.data.error_message;
                                this.submitting = false;
                            } else {
                                order.invoice_number = response.data.data.invoice_number;
                                order.transaction_id = response.data.data.transaction_id;
                                order.id = response.data.data.id;

                                this.$root.setFlashMessage('success', 'Your order has been successfully submitted.');
                                this.submitting = false;
                                this.$router.push({name: 'confirmation'});
                            }
                        } else {
                            this.checkoutError = 'Error processing credit card';
                            this.submitting = false;
                        }
                    }).catch(response => {
                        this.checkoutError = 'Critical details for processing this order could not be determined. Unfortunately you will need to restart the order process to continue.';
                        this.submitting = false;
                    });
            } catch (message) {
                this.checkoutError = message;
                this.submitting = false;
            }
            this.clearConfirmationLoaded();
        },

        refreshEstimate: (_.debounce(function() {
            if (!this.zipCode) {
                this.$root.estimate = {};
                return;
            }

            // Update the zip code pricing
            axios.get('/order/api/search', {
                params: {zip_code: this.zipCode.zip_code }
            }).then(response => {
                this.$root.zipCode = response.data.data;
            }).catch(response => {
                if (!response.data.data) {
                    this.checkoutError = "We do not currently deliver to zip code " + this.zipCode.zip_code + ".";
                } else {
                    this.checkoutError = "We're sorry, but there's been a problem looking up zip code " + this.zipCode.zip_code +  " in our database.";
                }
            });

            // Update the estimate
            axios.get('/order/api/estimate', {
                params: Object.assign(
                    { zip_code: this.zipCode.zip_code },
                    this.order
                )
            }).then(response => {
                this.$root.estimate = response.data.data;
            }).catch(response => {
                this.$root.estimate = {};
            });
        }, 400)),
    },

    mounted() {

        if (!this.zipcode) {
            if (this.order.zip_code) {
                axios.get('/order/api/search', {params: {zip_code: this.order.zip_code}})
                    .then(response => {
                        this.$root.zipCode  = response.data.data;
                        this.$root.zip_code = this.$root.zipCode.zip_code;
                    })
                    .catch(response => {
                        this.$router.push({name: 'form'});
                    });
            } else {
                this.$router.push({name: 'form'});
            }
        }

        if (!this.firstStepComplete) {
            this.$router.push({name: 'form'});
        }

        $('[data-toggle="popover"]').popover({
            html: true
        });

        // change credit card opacify based on user input
        var $cardNumber = $('input[name=card_number]');

        $cardNumber.on('keyup', function() {

            var $amex       = $('.amex');
            var $visa       = $('.visa');
            var $mastercard = $('.mastercard');
            var $discover   = $('.discover');
            var $img        = $('.credit-cards .icon img');

            if (this.value.match(/^4/)) {
                $img.not($visa).css('opacity', 0.3);
                $visa.css('opacity', 1);
            } else if (this.value.match(/^5/)) {
                $img.not($mastercard).css('opacity', 0.3);
                $mastercard.css('opacity', 1);
            } else if (this.value.match(/^6/)) {
                $img.not($discover).css('opacity', 0.3);
                $discover.css('opacity', 1);
            } else if (this.value.match(/^3/)) {
                $img.not($amex).css('opacity', 0.3);
                $amex.css('opacity', 1);
            } else if (!this.value) {
                $img.css('opacity', 1);
            } else {
                $img.css('opacity', 0.275);
            }
        });
    },

};
</script>